/**
 * Cart Membership Pricing System
 * Applies live membership discounts to cart displays
 */
class CartMembershipPricingCore {
  static DISCOUNT_TIERS = {
    'bronze': 5,
    'silver': 10, 
    'gold': 15,
    'trade': 20
  };
  
  static getCustomerTier() {
    // Read customer tags from body dataset, with a resilient localStorage fallback
    let customerTags = (document.body.dataset.customerTags || '').trim();
    if (customerTags) {
      try {
        localStorage.setItem('exposurepack_customer_tags', customerTags);
      } catch (e) {
        // Ignore storage errors
      }
    } else {
      try {
        customerTags = (localStorage.getItem('exposurepack_customer_tags') || '').trim();
      } catch (e) {
        customerTags = '';
      }
    }

    const tags = customerTags
      ? customerTags.toLowerCase().split(',').map(tag => tag.trim()).filter(Boolean)
      : [];
    
    if (tags.includes('trade')) return 'trade';
    if (tags.includes('gold')) return 'gold';
    if (tags.includes('silver')) return 'silver';
    if (tags.includes('bronze')) return 'bronze';
    
    return null;
  }
  
  static getDiscountPercent() {
    const tier = this.getCustomerTier();
    return tier ? this.DISCOUNT_TIERS[tier] : 0;
  }
  
  static isProductExcludedFromDiscount(cartItem) {
    // Check if this specific cart item should be excluded from discounts
    const productTitle = cartItem.querySelector('.cart-item__name, .product-option dt')?.textContent?.toLowerCase() || '';
    const cartItemLink = cartItem.querySelector('a')?.href?.toLowerCase() || '';
    
    // Exclusions: coasters and wholesale products
    const isCoaster = productTitle.includes('coaster') || cartItemLink.includes('coaster');
    const isWholesale = productTitle.includes('wholesale') || cartItemLink.includes('wholesale');
    
    if (isCoaster || isWholesale) {
      return true;
    }
    
    return false;
  }
  
  static formatPriceWithDiscount(originalPriceCents, tierName, showOriginal = true) {
    const discountPercent = this.DISCOUNT_TIERS[tierName] || 0;
    const discountedPriceCents = originalPriceCents * (1 - discountPercent / 100);
    
    // Format to money display
    const originalPrice = (originalPriceCents / 100).toFixed(2);
    const discountedPrice = (discountedPriceCents / 100).toFixed(2);
    
    if (showOriginal) {
      return `
        <span class="original-price">$${originalPrice}</span>
        <span class="member-price member-${tierName}">$${discountedPrice}</span>
      `;
    } else {
      return `<span class="member-price member-${tierName}">$${discountedPrice}</span>`;
    }
  }
  
  // When a real Shopify discount (automatic, code, or per-line allocation) is active,
  // Shopify's rendered numbers are authoritative; custom membership pricing must defer
  // so the cart/drawer totals reconcile to checkout instead of double-discounting.
  static shouldDeferToShopify() {
    return typeof window.epkHasShopifyDiscount === 'function' &&
           window.epkHasShopifyDiscount(window.__epkLastCart);
  }

  static applyCartDiscounts() {
    if (this.shouldDeferToShopify()) {
      console.log('[Cart Membership] Shopify discount active — deferring to Shopify prices, skipping member overwrite');
      return;
    }

    const tier = this.getCustomerTier();
    const discountPercent = this.getDiscountPercent();

    if (tier && discountPercent > 0) {
      // Apply discounts to cart drawer items
      this.applyCartDrawerDiscounts(tier, discountPercent);
      
      // Apply discounts to main cart page items
      this.applyMainCartDiscounts(tier, discountPercent);
      
      // Force update all price elements we can find
      this.forceUpdateAllPrices(tier, discountPercent);
    }
    
    // Always update cart totals (crucial for consistency)
    this.updateCartTotals(tier, discountPercent);
  }
  
  static applyCartDrawerDiscounts(tier, discountPercent) {
    // Target cart drawer price elements
    const cartDrawerItems = document.querySelectorAll('cart-drawer .cart-item, .drawer__contents .cart-item');
    
    cartDrawerItems.forEach(item => {
      // Skip if already processed
      if (item.dataset.membershipProcessed) return;
      
      // Check if this item should be excluded from discounts
      if (this.isProductExcludedFromDiscount(item)) {
        item.dataset.membershipProcessed = 'true';
        return;
      }
      
      // Find price elements in cart drawer (support multiple theme variants)
      const priceElements = item.querySelectorAll(
        '.cart-item__totals .price:not(.original-price):not(.member-price),\
         .cart-item__totals [data-cart-item-price]:not(.original-price):not(.member-price),\
         .cart-item__totals .cart-item__final-price:not(.original-price):not(.member-price),\
         .cart-item__totals .cart-item__price:not(.original-price):not(.member-price)'
      );
      
      priceElements.forEach(priceEl => {
        // Skip if this node already contains member/original spans or was processed
        if (priceEl.querySelector('.member-price, .original-price')) return;
        if (priceEl.dataset.membershipProcessed === 'true') return;
        if (priceEl.closest('.cart-item__discounted-prices')) return; // Skip already discounted items
        
        const priceText = priceEl.textContent.trim();
        const priceMatch = priceText.match(/\$?([\d,]+\.?\d{0,2})/);
        
        if (priceMatch) {
          const originalPrice = parseFloat(priceMatch[1].replace(/,/g, ''));
          const originalPriceCents = Math.round(originalPrice * 100);
          
          priceEl.innerHTML = this.formatPriceWithDiscount(originalPriceCents, tier, true);
          priceEl.dataset.membershipProcessed = 'true';
        }
      });
      
      item.dataset.membershipProcessed = 'true';
    });
  }
  
  static applyMainCartDiscounts(tier, discountPercent) {
    // Target main cart page price elements
    const cartItems = document.querySelectorAll('.cart-item:not([data-membership-processed])');
    
    cartItems.forEach(item => {
      // Check if this item should be excluded from discounts
      if (this.isProductExcludedFromDiscount(item)) {
        item.dataset.membershipProcessed = 'true';
        return;
      }
      
      // Do NOT touch product option rows (like Price/Unit) to avoid duplicate display
      
      // Process main item total price (broad selectors for variants)
      const totalPriceElements = item.querySelectorAll(
        '.cart-item__totals .price:not(.original-price):not(.member-price),\
         .cart-item__totals [data-cart-item-price]:not(.original-price):not(.member-price),\
         .cart-item__totals .cart-item__final-price:not(.original-price):not(.member-price),\
         .cart-item__totals .cart-item__price:not(.original-price):not(.member-price)'
      );
      
      totalPriceElements.forEach(priceEl => {
        // Skip if this node already contains member/original spans or was processed
        if (priceEl.querySelector('.member-price, .original-price')) return;
        if (priceEl.dataset.membershipProcessed === 'true') return;
        if (priceEl.closest('.cart-item__discounted-prices')) return; // Skip already discounted
        
        const priceText = priceEl.textContent.trim();
        const priceMatch = priceText.match(/\$?([\d,]+\.?\d{0,2})/);
        
        if (priceMatch) {
          const originalPrice = parseFloat(priceMatch[1].replace(/,/g, ''));
          const originalPriceCents = Math.round(originalPrice * 100);
          
          priceEl.innerHTML = this.formatPriceWithDiscount(originalPriceCents, tier, true);
          priceEl.dataset.membershipProcessed = 'true';
        }
      });
      
      item.dataset.membershipProcessed = 'true';
    });
  }
  
  static updateCartTotals(tier, discountPercent) {
    // Defer to Shopify's authoritative cart.total_price whenever a real discount is present.
    if (this.shouldDeferToShopify()) {
      console.log('[Cart Membership] Shopify discount active — leaving Shopify cart total intact');
      return;
    }

    // Calculate correct totals: actual final price vs true original price (before any discounts)
    const subtotalElements = document.querySelectorAll(
      '.totals__subtotal-value, .cart__total .price, [data-cart-subtotal], .drawer__footer .totals__subtotal-value'
    );
    
    const totalElements = document.querySelectorAll(
      '.totals__total-value:not(.member-price), .cart__checkout .price:not(.member-price), [data-cart-total]:not(.member-price), .drawer__footer .totals__total-value:not(.member-price)'
    );
    
    // Calculate totals by examining each cart item individually
    const cartItems = document.querySelectorAll('.cart-item');
    let finalTotal = 0;
    let originalTotal = 0;
    let hasAnyDiscounts = false;
    
    cartItems.forEach(item => {
      // For each item, get the member price (if it exists) and original price
      const memberPriceElement = item.querySelector('.cart-item__totals .member-price, .member-price');
      const originalPriceElement = item.querySelector('.cart-item__totals .original-price, .original-price');
      const regularPriceElement = item.querySelector('.cart-item__totals .price:not(.member-price):not(.original-price)');
      
      if (memberPriceElement && originalPriceElement) {
        // This item has a discount applied
        const memberPriceMatch = memberPriceElement.textContent.trim().match(/\$?([\d,]+\.?\d{0,2})/);
        const originalPriceMatch = originalPriceElement.textContent.trim().match(/\$?([\d,]+\.?\d{0,2})/);
        
        if (memberPriceMatch && originalPriceMatch) {
          const memberPrice = parseFloat(memberPriceMatch[1].replace(/,/g, ''));
          const originalPrice = parseFloat(originalPriceMatch[1].replace(/,/g, ''));
          
          finalTotal += memberPrice;
          originalTotal += originalPrice;
          hasAnyDiscounts = true;
        }
      } else if (regularPriceElement) {
        // This item has no discount (like coasters)
        const regularPriceMatch = regularPriceElement.textContent.trim().match(/\$?([\d,]+\.?\d{0,2})/);
        
        if (regularPriceMatch) {
          const regularPrice = parseFloat(regularPriceMatch[1].replace(/,/g, ''));
          
          finalTotal += regularPrice;
          originalTotal += regularPrice;
        }
      }
    });
    
    // If no cart items found, try to get total from Shopify's cart object
    if (finalTotal === 0 && window.cart && window.cart.total_price) {
      finalTotal = window.cart.total_price / 100; // Convert from cents
      originalTotal = finalTotal;
    }
    
    // Update total elements with correct pricing display
    [...subtotalElements, ...totalElements].forEach(element => {
      if (element.querySelector('.member-price') || element.querySelector('.original-price')) return; // Already processed
      
      if (finalTotal > 0) {
        if (hasAnyDiscounts && originalTotal > finalTotal) {
          // Show crossed out original and discounted final
          element.innerHTML = `
            <span class="original-price">$${originalTotal.toFixed(2)}</span>
            <span class="member-price member-${tier || 'gold'}">$${finalTotal.toFixed(2)}</span>
          `;
        } else {
          // No discounts, show regular price
          element.innerHTML = `$${finalTotal.toFixed(2)}`;
        }
      }
    });
  }
  
  static forceUpdateAllPrices(tier, discountPercent) {
    // Restrict to line-item totals only; never touch cart totals here
    const allPriceElements = document.querySelectorAll(
      '.cart-item__totals .price,\
       .cart-item__totals [data-cart-item-price],\
       .cart-item__totals .cart-item__final-price,\
       .cart-item__totals .cart-item__price'
    );
    
    allPriceElements.forEach((element, index) => {
      // Skip if already processed or already contains member/original spans
      if (element.querySelector('.member-price, .original-price')) {
        return;
      }
      if (element.dataset.membershipProcessed) return;

      // Skip elements that are inside the totals area to avoid cart-wide discounting
      if (element.closest('.totals')) return;
      
      // Check if this element is within an excluded cart item
      const cartItem = element.closest('.cart-item');
      if (cartItem && this.isProductExcludedFromDiscount(cartItem)) {
        element.dataset.membershipProcessed = 'true';
        return;
      }
      
      const text = element.textContent.trim();
      const priceMatch = text.match(/\$?([\d,]+\.?\d{0,2})/);
      
      if (priceMatch) {
        const originalPrice = parseFloat(priceMatch[1].replace(/,/g, ''));
        if (originalPrice > 0) {
          const originalPriceCents = Math.round(originalPrice * 100);
          
          if (tier && discountPercent > 0) {
            element.innerHTML = this.formatPriceWithDiscount(originalPriceCents, tier, true);
          }
          element.dataset.membershipProcessed = 'true';
        }
      }
    });
  }
  
  static ensureCartTotalsCorrect() {
    // Standalone method to always ensure cart totals are accurate
    // This can be called from any page to fix totals
    if (this.shouldDeferToShopify()) {
      console.log('[Cart Membership] Shopify discount active — not re-applying member totals');
      return;
    }

    const tier = this.getCustomerTier();
    const discountPercent = tier ? this.getDiscountPercent() : 0;
    
    // Make sure all cart items are properly processed first
    if (tier && discountPercent > 0) {
      // Re-apply discounts to ensure everything is up to date
      this.applyCartDrawerDiscounts(tier, discountPercent);
      this.applyMainCartDiscounts(tier, discountPercent);
    }
    
    // Always update totals regardless of membership status
    this.updateCartTotals(tier, discountPercent);
  }
  
  static addMembershipIndicator() {
    const tier = this.getCustomerTier();
    if (!tier) return;
    
    const discountPercent = this.getDiscountPercent();
    
    // Use a unique ID to prevent duplicates and track if we've already added one
    const INDICATOR_ID = 'cart-membership-indicator-unique';
    
    // Remove any existing indicators first to prevent duplicates
    const existingIndicator = document.getElementById(INDICATOR_ID);
    if (existingIndicator) {
      return;
    }
    
    // Also remove any old-style indicators without ID
    document.querySelectorAll('.cart-membership-indicator').forEach(indicator => {
      if (!indicator.id) {
        indicator.remove();
      }
    });
    
    // Priority order: when empty, place inside the empty state container so the badge stays at the very top.
    // Otherwise, fall back to cart drawer containers.
    const possibleContainers = [
      '.drawer__inner-empty',
      '.drawer__contents',
      '.drawer__inner',
      '.cart-items',
      '.main-cart'
    ];
    
    let targetContainer = null;
    for (const selector of possibleContainers) {
      targetContainer = document.querySelector(selector);
      if (targetContainer) {
        break;
      }
    }
    
    if (targetContainer) {
      const indicator = document.createElement('div');
      indicator.id = INDICATOR_ID;
      indicator.className = `cart-membership-indicator membership-${tier}`;
      indicator.innerHTML = `
        <div class="membership-status">
          <span class="membership-badge membership-${tier}">
            ${tier === 'bronze' ? '🥉' : tier === 'silver' ? '🥈' : tier === 'gold' ? '🥇' : '💎'}
          </span>
          <span class="membership-text">${tier.toUpperCase()} Member - ${this.getDiscountPercent()}% OFF applied!</span>
        </div>
      `;
      // Ensure the indicator renders at the top of the chosen container
      targetContainer.insertBefore(indicator, targetContainer.firstChild);
    }
  }
  
  static init() {
    // Skip on admin order pages
    if (document.getElementById('order-detail-root')) {
      return;
    }
    
    // Apply discounts immediately (or process cart without discounts)
    this.applyCartDiscounts();
    
    // Add membership indicator (only if customer has tier)
    const tier = this.getCustomerTier();
    if (tier) {
      this.addMembershipIndicator();
    }
    
    // Listen for cart updates
    document.addEventListener('cart:updated', () => {
      setTimeout(() => {
        // Reset processed flags
        document.querySelectorAll('[data-membership-processed]').forEach(el => {
          delete el.dataset.membershipProcessed;
        });
        
        this.applyCartDiscounts();
        // Always ensure totals are correct
        this.ensureCartTotalsCorrect();
        // Only re-add indicator if it doesn't exist and customer has tier
        const tier = this.getCustomerTier();
        if (tier && !document.getElementById('cart-membership-indicator-unique')) {
          this.addMembershipIndicator();
        }
      }, 100); // Small delay to ensure DOM is updated
    });
    
    // Listen for cart drawer opening
    document.addEventListener('cart-drawer:open', () => {
      setTimeout(() => {
        this.applyCartDiscounts();
        this.ensureCartTotalsCorrect();
        // Only add indicator if it doesn't exist and customer has tier
        const tier = this.getCustomerTier();
        if (tier && !document.getElementById('cart-membership-indicator-unique')) {
          this.addMembershipIndicator();
        }
        // Ensure any discount popup remains visible (do not focus-steal or re-render drawer)
        const discountPopup = document.querySelector('.cart-discount-popup, [data-discount-popup]');
        if (discountPopup) {
          discountPopup.style.display = '';
          discountPopup.classList.remove('hidden');
        }
      }, 200);
    });
    
    // Observe DOM changes for dynamic cart updates
    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      
      mutations.forEach(mutation => {
        if (mutation.type === 'childList') {
          // Check if cart items were added/removed
          const addedNodes = Array.from(mutation.addedNodes);
          const removedNodes = Array.from(mutation.removedNodes);
          
          if (addedNodes.some(node => 
            node.nodeType === 1 && (
              node.classList?.contains('cart-item') || 
              node.querySelector?.('.cart-item') ||
              node.classList?.contains('price')
            )
          ) || removedNodes.some(node => 
            node.nodeType === 1 && (
              node.classList?.contains('cart-item') || 
              node.querySelector?.('.cart-item')
            )
          )) {
            shouldUpdate = true;
          }
        }
      });
      
      if (shouldUpdate) {
        setTimeout(() => this.applyCartDiscounts(), 50);
      }
    });
    
    // Observe cart containers
    const cartContainers = document.querySelectorAll(
      'cart-drawer, .cart-drawer, .cart-items, .main-cart, .drawer__contents'
    );
    
    cartContainers.forEach(container => {
      if (container) {
        observer.observe(container, {
          childList: true,
          subtree: true
        });
      }
    });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  (window.CartMembershipPricing || CartMembershipPricingCore).init();
});

// Also initialize on page load in case of timing issues
window.addEventListener('load', function() {
  (window.CartMembershipPricing || CartMembershipPricingCore).init();
});

// Listen for cart drawer clicks specifically
document.addEventListener('click', function(e) {
  if (e.target.closest('[href*="cart"], [data-cart], .cart-count-bubble, .cart-icon')) {
    setTimeout(() => {
      (window.CartMembershipPricing || CartMembershipPricingCore).init();
      (window.CartMembershipPricing || CartMembershipPricingCore).ensureCartTotalsCorrect();
    }, 500);
  }
});

// Try initialization every 2 seconds for the first 10 seconds (in case of timing issues)
let cartMembershipInitAttempts = 0;
const cartMembershipInitInterval = setInterval(() => {
  cartMembershipInitAttempts++;
  (window.CartMembershipPricing || CartMembershipPricingCore).init();
  
  if (cartMembershipInitAttempts >= 5) {
    clearInterval(cartMembershipInitInterval);
  }
}, 2000);

// Make it globally available
window.CartMembershipPricing = window.CartMembershipPricing || CartMembershipPricingCore;
